<template>
    <div id="quiz-creator-modal">
        <div
            v-if="quiz.type"
            :class="[{ 'modal fade p-0': true, show: showModal, close: showModal === false }]"
            id="quiz-config"
        >
            <div @click="closeModal()" class="bg-backdrop absolute w-full h-full"></div>
            <div class="modal-content relative flex flex-col overflow-hidden">
                <div class="modal-header flex justify-between items-center p-4 w-full fixed sticky">
                    <h3 class="m-0 shadow-text-none">
                        {{
                            !isEdit
                                ? $t('setting_modal_header_create_quiz', {
                                      type: quiz.type.charAt(0).toUpperCase() + quiz.type.slice(1)
                                  })
                                : $t('setting_modal_header_edit_quiz', {
                                      type: quiz.type.charAt(0).toUpperCase() + quiz.type.slice(1)
                                  })
                        }}
                    </h3>
                    <div @click="closeModal()" class="close-btn-box flex justify-center items-center">
                        <i class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
                    </div>
                </div>
                <div v-if="showContent" class="modal-body flex-1 relative z-10">
                    <div class="w-full h-full overflow-y-auto pb-16">
                        <QuizSection
                            @delete-quiz="$emit('delete-quiz', $event)"
                            @open-share-validate-modal="openShareValidateModal($event)"
                            @open-share-popup="$emit('open-share-popup', $event)"
                            @open-log="showActivityLog()"
                            :quiz="quiz"
                            ref="quizSection"
                        />
                        <QuestionSection
                            @open-config="openQuizSectionModal($event)"
                            @completed="saveQuizSuccess($event)"
                            @update-questions="updateTempQuestions($event)"
                            @open-approval-modal="showApprovalModal = true"
                            @open-share-validate-modal="openShareValidateModal($event)"
                            @processing="processing = $event"
                            ref="questionSection"
                        />
                    </div>

                    <QuizCreatorLogSidebar v-if="quiz._id && showLog" @close="closeActivityLog" :id="quiz._id" />
                </div>
            </div>

            <transition name="fade">
                <UploadImageModal
                    v-if="uploadMedia.open && isLoggedIn()"
                    @open="onOpenModalUpload"
                    @close="onCloseModalUpload"
                    @setImage="uploadImage"
                    @setVideo="uploadQuestionVideo"
                    @not-paid="whenNotpaid"
                    @select-ai="showAIGenerateImageModal = true"
                    @close-ai="
                        showAIGenerateImageModal = false
                        showGenerateBgModal = false
                    "
                    :question-video="uploadMedia.questionVideo"
                    :is-paid-user="isPaidUser"
                    :show-giphy="isSupportGifs"
                    ref="openAsUpload"
                />
            </transition>

            <AIGenerateImageModal
                v-if="showAIGenerateImageModal"
                @image-selected="imageAISelected"
                @close="showAIGenerateImageModal = false"
                :title="aiImageTitle"
                :location="uploadMedia.location"
            />

            <AuthPopup
                v-if="showAuthPopup"
                @login-success="loginSuccess"
                @register-success="loginSuccess"
                @close="showAuthPopup = false"
                @open="showAuthPopup = true"
                :webview-redirect-to="createWebviewRedirectTo()"
                ref="authPopup"
            />

            <ApprovalPopup v-if="showApprovalModal" @submit="submitApproval()" @close="showApprovalModal = false" />

            <ShareValidateModal
                v-if="showShareValidateModal"
                @close="showShareValidateModal = false"
                :validate-data="validateData"
                :show-remove-description="showRemoveDescription"
            />

            <QuizGenerateBgModal
                v-if="showGenerateBgModal"
                @close="showGenerateBgModal = false"
                @image-selected="imageAISelected($event, 'themebgimage')"
                :image="quiz.image"
                ref="genBgModal"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import quizCreator from '@/mixins/quiz-creator.js'
import QuizSection from '@/components/quiz-creator/quiz-section/QuizSection'
import QuestionSection from '@/components/quiz-creator/question-section/QuestionSection'
import popup from '@/lib/popup.js'
import UploadImageModal from '@/components/UploadImageModal'
import upload from '@/lib/upload.js'
import AuthPopup from '@/components/AuthPopup'
import AuthPopupMixin from '@/mixins/auth-popup'
import { isLoggedIn, currentUser, validateToken } from '@/lib/user.js'
import ApprovalPopup from '@/components/ApprovalPopup.vue'
import helperMixins from '@/mixins/helper.js'
import IntroHint from '@/lib/intro.js'
import { startIntroQuizCreator } from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'
import appConfig from '@/app.config.js'
import ShareValidateModal from '@/components/quiz-creator/ShareValidateModal'
import AIGenerateImageModal from '@/components/AIGenerateImageModal'
import ExampleQuiz from '@/mixins/example-quiz'
import QuizCreatorLogSidebar from '@/components/quiz-creator/QuizCreatorLogSidebar'
import quizCreatorWatcher from '@/mixins/quiz-creator-watcher.js'
import QuizGenerateBgModal from '@/components/quiz-creator/QuizGenerateBgModal'

export default {
    components: {
        QuizSection,
        QuestionSection,
        UploadImageModal,
        AuthPopup,
        ApprovalPopup,
        ShareValidateModal,
        AIGenerateImageModal,
        QuizCreatorLogSidebar,
        QuizGenerateBgModal
    },

    mixins: [quizCreator, AuthPopupMixin, helperMixins, introJsData, ExampleQuiz, quizCreatorWatcher],

    props: {
        quizzesLength: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            showTypeSelectModal: false,
            showModal: null,
            showContent: false,
            quizId: null,
            quizTemp: null,
            isEdit: false,
            showAuthPopup: false,
            showApprovalModal: false,
            desktopDelay: 300,
            mobileDelay: 500,
            introHint: new IntroHint(),
            isUploadBehavior: false,
            isSupportGifs: true,
            showShareValidateModal: false,
            validateData: null,
            showAIGenerateImageModal: false,
            processing: false,
            showRemoveDescription: false,
            showLog: false,
            showGenerateBgModal: false
        }
    },

    created() {
        this.introHint.removeHints()

        this.$store.commit('resetQuiz')

        this.$store.state.question.type = ''

        if (!this.quiz.title) {
            this.$store.commit('updateSetting', { value: 'Untitled', field: 'title' })
        }

        if (!this.quiz.image && !this.quiz.image_placeholder) {
            this.$store.commit('updateQuizPlaceholderImage', this.randomPlaceholderImage())
        }

        this.quizTemp = JSON.stringify(this.quiz)
    },

    mounted() {
        window.onbeforeunload = function () {
            return 'Some question required. Do you want to edit or close? if close unfilled questions will not be saved'
        }
    },

    destroyed() {
        window.onbeforeunload = function () {}
    },

    watch: {
        uploadMedia: {
            handler(data) {
                this.$nextTick(() => {
                    if (data.open) {
                        if (isLoggedIn()) {
                            this.getQuestionType(data.questionIndex)
                            this.$refs.openAsUpload.setQuestionIndex(data.questionIndex)
                            this.$refs.openAsUpload.setAnswerIndex(data.answerIndex)

                            if (data.isEdit) {
                                if (data.questionVideo) {
                                    this.$refs.openAsUpload.editVideo(data)
                                } else {
                                    this.$refs.openAsUpload.editImage(data)
                                }
                            }
                        } else {
                            this.showAuthPopup = true
                        }
                    }
                })
            },
            deep: true
        },

        quizStatusUpdated(val) {
            if (!this.showContent) return

            this.quizTemp = JSON.parse(this.quizTemp)

            this.quizTemp.status = val

            this.setCacheData(this.quizTemp)

            this.quizTemp = JSON.stringify(this.quizTemp)
        }
    },

    computed: {
        ...mapState({
            quiz: state => state.question,
            uploadMedia: state => state.uploadMedia,
            showSchoolList: state => state.question.show_school_list,
            showSchoolCountryList: state => state.question.show_school_country_list
        }),

        ...mapGetters({
            themes: 'themes',
            themeName: 'themeName',
            orderAbility: 'orderAbility',
            paymentModalTitle: 'paymentModalTitle',
            paymentModalDescription: 'paymentModalDescription',
            quizStatusUpdated: 'getQuizStatus'
        }),

        isPaidUser() {
            return this.orderAbility.is_24hrs_access || this.orderAbility.has_subscribed
        },

        aiImageTitle() {
            const { questionIndex, answerIndex } = this.uploadMedia

            const mappers = {
                quiz: this.quiz.title || '',
                question: (this.quiz.questions[questionIndex] && this.quiz.questions[questionIndex].title) || '',
                answer:
                    (this.quiz.questions[questionIndex] &&
                        this.quiz.questions[questionIndex].answers[answerIndex] &&
                        this.quiz.questions[questionIndex].answers[answerIndex].title) ||
                    '',
                themebgimage: this.quiz.title || ''
            }

            return mappers[this.uploadMedia.location]
        }
    },

    methods: {
        getQuestionType(questionIndex) {
            if (this.quiz.questions[questionIndex]) {
                this.isSupportGifs =
                    this.quiz.questions[questionIndex].question_type != 'puzzle' &&
                    this.quiz.questions[questionIndex].question_type != 'hotspot'
            }
        },

        closeModal(requestUser) {
            if (this.processing) return

            let notEqual = false

            const quizTempBeforeProcess = this.quizTemp

            if (typeof this.quizTemp == 'string') {
                this.quizTemp = JSON.parse(this.quizTemp)
            }

            if (!this.quizTemp) {
                this.closeModalAnimation()
                return
            }

            if (!this.quiz.questions.length) {
                this.quizTemp.questions = []
            }

            const quizTemp = Object.getOwnPropertyNames(this.quizTemp)

            quizTemp.splice(
                quizTemp.findIndex(e => e === '__ob__'),
                1
            )

            for (const propName of quizTemp) {
                if (!this.quizTemp[propName] && propName == 'background_type') {
                    this.quizTemp[propName] = 'color'
                }

                if (!this.quiz[propName] && propName == 'background_type') {
                    this.quiz[propName] = 'color'
                }

                if (propName == 'background_music') {
                    if (!this.quizTemp[propName].name) {
                        this.quizTemp[propName].name = 'No Music'
                    }

                    if (!this.quiz[propName].name) {
                        this.quiz[propName].name = 'No Music'
                    }
                }

                if (!this.quizTemp[propName] && this.quizTemp[propName] !== false) {
                    this.quizTemp[propName] = null
                }

                if (!this.quiz[propName] && this.quiz[propName] !== false) {
                    this.quiz[propName] = null
                }

                if (
                    !['tags'].includes(propName) &&
                    JSON.stringify(this.quizTemp[propName]) !== JSON.stringify(this.quiz[propName])
                ) {
                    notEqual = true
                    break
                }
            }

            if (notEqual) {
                popup.confirm(
                    this.$t('setting_confirm_quiz_unsaved'),
                    () => {
                        this.removeCacheQuiz(this.quiz)
                        this.closeModalAnimation(requestUser)
                        this.introHint.removeHints()
                    },
                    async () => {
                        const save = await this.$refs.questionSection.saveQuiz()

                        if (save) {
                            this.closeModalAnimation(requestUser)
                            this.introHint.removeHints()
                        }
                    },
                    {
                        customButtons: {
                            ok: {
                                text: this.$t('quiz_creator_close_without_saving'),
                                className: 'btn btn-custom-padding default font-opensans text-md'
                            },
                            reject: {
                                text: this.$t('quiz_creator_save_and_close'),
                                className:
                                    'btn btn-custom-padding font-opensans text-md system-button-color system-button-text-color'
                            },
                            cancel: {
                                callback: () => {
                                    this.quizTemp = quizTempBeforeProcess
                                }
                            }
                        },

                        className: 'quiz-creator-confirm-popup'
                    }
                )

                return
            }

            this.removeCacheQuiz(this.quiz)
            this.closeModalAnimation(requestUser)
            this.introHint.removeHints()
        },

        closeModalAnimation(requestUser) {
            this.showTypeSelectModal = false
            this.showModal = false

            setTimeout(
                () => {
                    if (this.quiz._id) {
                        if (requestUser) {
                            this.acceptRequest(requestUser)
                        } else {
                            this.watcherExit()
                        }
                    }

                    this.showContent = false
                    this.$emit('close')
                },
                window.innerWidth <= 767 ? this.mobileDelay : this.desktopDelay
            )
        },

        selectQuizType(type, quizLength) {
            if (isLoggedIn()) {
                this.setUserToQuiz(currentUser())
            }

            if (typeof quizLength == 'undefined') {
                quizLength = this.quizzesLength
            }

            if (!this.isPaidUser && quizLength >= appConfig.maxFreeUserQuiz) {
                this.$store.commit('setPaymentModalTitleDescription', {
                    title: this.$t('my_quiz_payment_modal_create_more_quiz_title'),
                    description: this.$t('my_quiz_payment_modal_create_more_quiz_description')
                })

                this.$store.commit('openPaymentPopup', true)

                return
            }

            if (type == 'quiz') {
                this.$store.commit('setCreateNewQuiz', true)
            }

            if (window.defaultThemes && window.defaultThemes().theme_1) {
                this.quiz.themes = JSON.parse(JSON.stringify(window.defaultThemes()))
            }

            this.quiz.type = type
            this.quizTemp = JSON.stringify(this.quiz)

            this.getCacheQuiz(this.quiz)

            this.$nextTick(() => {
                this.showModal = true
                this.showContent = true

                if (window.innerWidth <= 767) {
                    setTimeout(() => {
                        this.showTypeSelectModal = false
                    }, this.mobileDelay)
                } else {
                    this.showTypeSelectModal = false
                }
            })
        },

        async editQuiz(quizId) {
            this.quizId = quizId
            this.isEdit = true

            await this.getQuiz()

            this.openQuizCreator()
        },

        getQuiz() {
            return new Promise(async (resolve, reject) => {
                if (this.quizId == 'mock') {
                    this.quiz.type = 'quiz'

                    const quiz = {
                        ...{ _id: '' },
                        ...{ user_id: currentUser().id },
                        ...{
                            username: ['teacher', 'teacher_plus', 'student', 'student_plus'].includes(
                                currentUser().role
                            )
                                ? currentUser().name
                                : currentUser().username
                        },
                        ...{ email: currentUser().email },
                        ...this.exampleQuiz()
                    }

                    if (currentUser().school_id) {
                        quiz.school_id = currentUser().school_id
                    }

                    this.$store.commit('updateQuestion', quiz)

                    this.quizTemp = JSON.stringify(this.quiz)

                    resolve()
                    return
                }

                const quiz = await this.$store.dispatch('fetchQuestionById', this.quizId)

                if (quiz.status === 200) {
                    if (isLoggedIn() && currentUser().id === quiz.user_id) {
                        this.setUserToQuiz(currentUser())
                    }

                    this.quizTemp = JSON.stringify(this.quiz)

                    resolve()
                    return
                }

                reject()
            })
        },

        openQuizCreator() {
            this.$nextTick(() => {
                this.showModal = true
                this.showContent = true
                this.$store.commit('toggleMainLoader', false)

                if (this.quiz.share_setting && this.quiz.share_setting.can_edit) {
                    this.watcherInit()
                } else {
                    this.$store.commit('enableToEditQuiz', true)

                    setTimeout(() => {
                        startIntroQuizCreator(this.stepTour2)
                    }, 500)
                }
            })
        },

        showLoadingImage(questionIndex, answerIndex, status) {
            let imageElement

            if (this.uploadMedia.location == 'quiz') {
                imageElement = this.$refs.quizSection.$refs.QuizConfigSetting.$refs.quizImage

                if (!imageElement) return

                imageElement.uploadingImage = status
            }

            if (this.uploadMedia.location == 'background') {
                imageElement = this.$refs.quizSection.$refs.themeSetting.$refs.bgImage

                if (!imageElement) return

                imageElement.uploadingImage = status
            }

            if (this.uploadMedia.location == 'question') {
                imageElement =
                    this.$refs.questionSection.$refs.questionSettingModal.$refs['questionSettingSection-0'][0].$refs
                        .questionImage

                if (!imageElement) return

                imageElement.show = status
                imageElement.uploadingImage = status
            }

            if (this.uploadMedia.location == 'answer') {
                imageElement =
                    this.$refs.questionSection.$refs.questionSettingModal.$refs['answerSettingSection-0'][0].$refs
                        .answerSection.$refs[`answer-image-${answerIndex}`]

                if (!imageElement) return

                imageElement[0].show = status
                imageElement[0].uploadingImage = status
            }

            if (this.uploadMedia.location == 'themebgimage') {
                imageElement = this.$refs.quizSection.$refs.themeSetting.$refs.themeImagePickerRef

                if (!imageElement) return

                imageElement.uploadingImage = status
            }
        },

        uploadImage(file, questionIndex, answerIndex, ai) {
            if (!file || !file.target.files.length) return false

            this.isUploadBehavior = true

            // this.showLoadingImage(questionIndex, answerIndex, true)

            const pixelFile = file.target.files[1] || false
            file = file.target.files[0]

            const validateFileSuccess = () => {
                const reader = new FileReader()

                reader.readAsDataURL(file)

                reader.onload = e => {
                    const formData = new FormData()
                    formData.append('image', file)

                    if (file.image_original) {
                        formData.append('image_original', file.image_original)
                    }

                    // Hack
                    if (file.name === 'image.gif' && pixelFile) {
                        formData.append('image', pixelFile)
                    }

                    if (this.isLoggedIn()) {
                        if (this.uploadMedia.location == 'quiz') {
                            this.$store
                                .dispatch('uploadQuizImage', formData)
                                .then(() => {
                                    if (ai && process.env.VUE_APP_GENERATE_BACKGROUND === 'true') {
                                        popup.confirm(
                                            this.$t('quiz_creator_confirm_generate_background_message'),
                                            async () => {
                                                this.showGenerateBgModal = true

                                                await this.$nextTick()

                                                this.$refs.genBgModal.genBg()
                                            },
                                            () => {},
                                            {
                                                icon: `${appConfig.baseUrl}/images/ai-image.svg`
                                            }
                                        )
                                    }
                                })
                                .catch(() => {
                                    this.showLoadingImage(questionIndex, answerIndex, false)
                                })
                        }

                        if (this.uploadMedia.location == 'background') {
                            this.$store.dispatch('uploadBackgroundImage', formData)
                        }

                        if (this.uploadMedia.location == 'question') {
                            const params = {
                                question_type: this.quiz.questions[questionIndex].question_type
                            }

                            this.$store
                                .dispatch('uploadQuestionImage', { formData, questionIndex, params })
                                .then(() => {})
                                .catch(() => {
                                    this.showLoadingImage(questionIndex, answerIndex, false)
                                })
                        }

                        if (this.uploadMedia.location == 'answer') {
                            this.$store
                                .dispatch('uploadAnswerImage', { formData, questionIndex, answerIndex })
                                .then(() => {})
                                .catch(() => {
                                    this.showLoadingImage(questionIndex, answerIndex, false)
                                })
                        }

                        if (this.uploadMedia.location == 'themebgimage') {
                            this.$store
                                .dispatch('uploadThemeBgImage', { formData, coordinates: file.coordinates })
                                .then(() => {})
                                .catch(() => {
                                    this.showLoadingImage(questionIndex, answerIndex, false)
                                })
                        }
                    } else {
                        if (this.uploadMedia.location == 'quiz') {
                            this.$store.commit('updateQuizImage', e.target.result)
                        }

                        if (this.uploadMedia.location == 'background') {
                            this.$store.commit('updateBackgroundImage', e.target.result)
                        }

                        if (this.uploadMedia.location == 'question') {
                            this.$store.commit('updateQuestionImage', { questionIndex, imagePath: e.target.result })
                        }

                        if (this.uploadMedia.location == 'answer') {
                            this.$store.commit('updateAnswerImage', {
                                questionIndex,
                                answerIndex,
                                imagePath: e.target.result
                            })
                        }
                    }

                    if (file.external) {
                        if (this.uploadMedia.location == 'quiz') {
                            this.$store.commit('updateExternalQuizImageSource', file.source)
                            this.$store.commit('updateQuizOriginalImage', file.image_original)
                        }

                        if (this.uploadMedia.location == 'background') {
                            this.$store.commit('updateExternalBackgroundImage', file.source)
                            this.$store.commit('updateQuizOriginalBackgroundImage', file.image_original)
                        }

                        if (this.uploadMedia.location == 'question') {
                            this.$store.commit('updateQuestionExternalImageSource', {
                                questionIndex,
                                source: file.source
                            })

                            this.$store.commit('updateQuestionOriginalImage', {
                                questionIndex,
                                original: file.image_original
                            })
                        }

                        if (this.uploadMedia.location == 'answer') {
                            this.$store.commit('updateAnswerExternalImageSource', {
                                questionIndex,
                                answerIndex,
                                source: file.source
                            })
                            this.$store.commit('updateAnswerOriginalImage', {
                                questionIndex,
                                answerIndex,
                                original: file.image_original
                            })
                        }

                        if (this.uploadMedia.location == 'themebgimage') {
                            const themes = this.themes
                            themes[this.themeName].background_image_source = file.source
                            themes[this.themeName].background_image_original = file.image_original

                            this.$store.commit('setThemes', themes)
                        }
                    } else {
                        if (typeof file.image_original == 'object') {
                            const formData2 = new FormData()

                            formData2.append('image', file.image_original[0])

                            if (this.isLoggedIn()) {
                                if (this.uploadMedia.location == 'quiz') {
                                    this.$store
                                        .dispatch('uploadQuizOriginalImage', formData2)
                                        .then(() => {})
                                        .catch(() => {
                                            this.showLoadingImage(questionIndex, answerIndex, false)
                                        })
                                }

                                if (this.uploadMedia.location == 'background') {
                                    this.$store.dispatch('uploadQuizBackgroundOriginalImageFile', formData2)
                                    this.backgroundOriginalImageFile = null
                                }

                                if (this.uploadMedia.location == 'question') {
                                    this.$store
                                        .dispatch('uploadQuestionOriginalImage', { formData2, questionIndex })
                                        .then(() => {})
                                        .catch(() => {
                                            this.showLoadingImage(questionIndex, answerIndex, false)
                                        })
                                }

                                if (this.uploadMedia.location == 'answer') {
                                    this.$store
                                        .dispatch('uploadAnswerOriginalImage', {
                                            formData2,
                                            questionIndex,
                                            answerIndex
                                        })
                                        .then(() => {})
                                        .catch(() => {
                                            this.showLoadingImage(questionIndex, answerIndex, false)
                                        })
                                }

                                if (this.uploadMedia.location == 'themebgimage') {
                                    this.$store
                                        .dispatch('uploadOriginalThemeBgImage', formData2)
                                        .then(() => {})
                                        .catch(() => {
                                            this.showLoadingImage(questionIndex, answerIndex, false)
                                        })
                                }
                            } else {
                                // Convert to data-url image when not logged in
                                const originalImageFileReader = new FileReader()

                                originalImageFileReader.readAsDataURL(file.image_original[0])

                                originalImageFileReader.onload = original => {
                                    if (this.uploadMedia.location == 'quiz') {
                                        this.$store.commit('updateQuizOriginalImage', original.target.result)
                                    }

                                    if (this.uploadMedia.location == 'background') {
                                        this.$store.commit('updateQuizOriginalBackgroundImage', original.target.result)
                                    }

                                    if (this.uploadMedia.location == 'question') {
                                        this.$store.commit('updateQuestionOriginalImage', {
                                            questionIndex,
                                            original: original.target.result
                                        })
                                    }

                                    if (this.uploadMedia.location == 'answer') {
                                        this.$store.commit('updateAnswerOriginalImage', {
                                            questionIndex,
                                            answerIndex,
                                            original: original.target.result
                                        })
                                    }
                                }
                            }
                        } else {
                            if (this.uploadMedia.location == 'quiz') {
                                this.$store.commit('updateQuizOriginalImage', file.image_original)
                            }

                            if (this.uploadMedia.location == 'background') {
                                this.$store.commit('updateQuizOriginalBackgroundImage', file.image_original)
                            }

                            if (this.uploadMedia.location == 'question') {
                                this.$store.commit('updateQuestionOriginalImage', {
                                    questionIndex,
                                    original: file.image_original
                                })
                            }
                            if (this.uploadMedia.location == 'answer') {
                                this.$store.commit('updateAnswerOriginalImage', {
                                    questionIndex,
                                    answerIndex,
                                    original: file.image_original
                                })
                            }
                        }
                    }

                    if (this.uploadMedia.location == 'quiz') {
                        this.$store.commit('updateQuizCoordinates', file.coordinates)
                    }

                    if (this.uploadMedia.location == 'background') {
                        this.$store.commit('updateQuizBackgroundCoordinates', file.coordinates)
                    }

                    if (this.uploadMedia.location == 'question') {
                        this.$store.commit('updateQuestionCoordinates', {
                            questionIndex,
                            coordinates: file.coordinates
                        })
                    }

                    if (this.uploadMedia.location == 'answer') {
                        this.$store.commit('updateAnswerCoordinates', {
                            questionIndex,
                            answerIndex,
                            coordinates: file.coordinates
                        })
                    }
                }
            }

            const validateFileFail = () => {
                this.showLoadingImage(questionIndex, answerIndex, false)

                if (this.uploadMedia.location == 'quiz') {
                    this.removeQuizImage()
                }

                if (this.uploadMedia.location == 'background') {
                    this.removeBackgroundImage()
                }

                if (this.uploadMedia.location == 'question') {
                    this.removeQuestionImage()
                }

                if (this.uploadMedia.location == 'answer') {
                    this.removeAnswerImage()
                }
            }

            if (
                this.uploadMedia.location == 'question' &&
                file.external &&
                this.quiz.questions[questionIndex].question_type == 'video'
            ) {
                this.$store.commit('updateQuestionImage', { questionIndex, imagePath: file.img_url })
            } else {
                upload.addFile(file).validate(validateFileSuccess, validateFileFail)
            }
        },

        async uploadQuestionVideo(file, videoTimeDuration, videoMute, videoLoop, videoProvider, questionIndex) {
            if (!file) return false

            this.isUploadBehavior = true

            if (file.target) {
                file = file.target.files[0]
            }

            this.$store.commit('removeQuestionVideo', questionIndex)

            const validateFileSuccess = () => {
                // Upload image to server
                const formData = new FormData()
                formData.append('image', file)
                this.$store
                    .dispatch('uploadQuestionVideo', { formData, questionIndex })
                    .then(() => {})
                    .catch(() => {
                        this.removeQuestionVideo()
                        setTimeout(() => {
                            this.$store.commit('removeQuestionImage', questionIndex)
                        }, 300)
                    })
                this.$store.commit('updateVideoTime', { questionIndex, videoTimeDuration })
                this.$store.commit('updateVideoMute', { questionIndex, videoMute })
                this.$store.commit('setLoopVideo', { videoLoop, questionIndex })
                this.$store.commit('updateVideoProvider', { questionIndex, videoProvider })
            }

            const validateFileFail = () => {
                this.removeQuestionVideo()
            }

            if (file.external) {
                this.$store.commit('updateQuestionVideo', { questionIndex, videoPath: file.video })
                this.$store.commit('updateVideoTime', { questionIndex, videoTimeDuration })
                this.$store.commit('updateVideoMute', { questionIndex, videoMute })
                this.$store.commit('setLoopVideo', { videoLoop, questionIndex })
                this.$store.commit('updateVideoProvider', { questionIndex, videoProvider })
            } else {
                upload.addFile(file).validateVideo(validateFileSuccess, validateFileFail)
            }
        },

        afterLoginSuccess(user) {
            this.setUserToQuiz(user)
            this.findOrderAbility()
        },

        setUserToQuiz(user) {
            this.$store.commit('updateSetting', {
                value: user.name,
                field: 'username'
            })

            this.$store.commit('updateSetting', {
                value: user.email,
                field: 'email'
            })

            this.$store.commit('updateSetting', {
                value: user.id,
                field: 'user_id'
            })

            if (!this.quiz.school_id && user.school_id) {
                this.$store.commit('updateSchoolId', user.school_id)
            }
        },

        findOrderAbility() {
            this.$store.dispatch('findOrderAbility')
        },

        openQuizSectionModal(type) {
            if (type == 'quiz') {
                this.$refs.quizSection.showQuizConfigSetting = true
            }

            if (type == 'theme') {
                this.$refs.quizSection.showThemeSetting = true
            }
        },

        async submitApproval() {
            const validate = await this.$refs.questionSection.validateQuiz()

            if (!validate) return

            let save
            const userService = await validateToken()

            if (userService) {
                await this.$refs.questionSection.reCaptchaValidate()
            }

            this.$store.commit('updateApprovalStatus', 'pending')

            if (!this.quizId) {
                save = await this.$store.dispatch('createQuestion')
            } else {
                save = await this.$store.dispatch('updateQuiz')
            }

            if (save) {
                popup.alert(this.$t('question_alert_submit_approval'), () => {
                    this.getQuiz()
                })
            }
        },

        saveQuizSuccess(quizData) {
            this.$emit('fetch-quiz', quizData.newQuiz)
            this.quizTemp = JSON.stringify(this.quiz)

            const quizIdTemp = this.quiz._id

            if (quizData.newQuiz) {
                if (this.quiz.type == 'quiz') {
                    this.quiz._id = 'cache-quiz'
                }

                if (this.quiz.type == 'poll') {
                    this.quiz._id = 'cache-poll'
                }
            }

            this.removeCacheQuiz(this.quiz)

            this.quiz._id = quizIdTemp

            this.quizId = this.quiz._id
        },

        updateTempQuestions(questions) {
            this.$store.commit('updateQuizQuestions', questions)

            if (typeof this.quizTemp == 'string') {
                this.quizTemp = JSON.parse(this.quizTemp)
            }

            this.quizTemp.questions = questions
            this.quizTemp = JSON.stringify(this.quizTemp)
        },

        onOpenModalUpload() {
            this.introHint.removeHints()
        },

        onCloseModalUpload(index) {
            this.uploadMedia.open = false

            if (index == 0) {
                if (this.isUploadBehavior == false) {
                    this.callHints(500)
                } else {
                    this.callHints(1400)
                }
            }

            setTimeout(() => {
                this.introHint.register(this.hintsThemeSettings)
            }, 500)

            this.isUploadBehavior = false
        },

        callHints(timeout) {
            setTimeout(() => {
                this.introHint.register(this.hintsCreateQuestion)
            }, timeout)
        },

        whenNotpaid() {
            const mediaObject = {
                open: false
            }

            this.$store.commit('toggleOpenUploadMedia', mediaObject)
            this.$store.commit('setPaymentModalTitleDescription', {
                title: this.$t('my_quiz_payment_model_need_premium_feature_title'),
                description: this.$t('my_quiz_payment_model_need_premium_feature_description')
            })
            this.$store.commit('openPaymentPopup', true)
        },

        paymentSuccess() {
            this.$store.dispatch('findOrderAbility')
        },

        onClosePaymentPopup() {
            this.$store.commit('openPaymentPopup', false)
        },

        async openShareValidateModal(data) {
            this.$store.commit('toggleMainLoader', true)

            this.validateData = data

            if (this.showSchoolList || this.showSchoolCountryList) {
                this.$store.commit('updateShowSchoolList', false)
                this.$store.commit('updateShowSchoolCountryList', false)

                const userService = await validateToken()

                if (userService) {
                    await this.$refs.questionSection.reCaptchaValidate()
                }

                this.quizTemp = JSON.parse(this.quizTemp)

                this.quizTemp.show_school_list = false
                this.quizTemp.show_school_country_list = false

                await this.$store.dispatch('updateQuiz', { quiz: this.quizTemp, saveOnlyShareToSchool: true })

                delete this.quizTemp.captcha_response

                this.setCacheData(this.quizTemp)

                this.quizTemp = JSON.stringify(this.quizTemp)

                this.showRemoveDescription = true
            } else {
                this.showRemoveDescription = false
            }

            await this.$nextTick()

            this.showShareValidateModal = true

            this.$store.commit('toggleMainLoader', false)
        },

        async imageAISelected(url, location) {
            url = url.replace(':8001', '')

            //hack create dom
            const dom = document.createElement('div')
            dom.setAttribute('data-img', url)
            dom.setAttribute('data-source', url)
            dom.setAttribute('data-thumbnail', url)

            const obj = {
                target: dom
            }

            if (!this.$refs.openAsUpload) {
                const mediaObject = {
                    open: true,
                    location
                }

                this.$store.commit('toggleOpenUploadMedia', mediaObject)
            }

            await this.$nextTick()

            this.$refs.openAsUpload.cropTab = true

            this.$refs.openAsUpload.selectExternalImage(obj)
        },

        showActivityLog() {
            this.introHint.removeHints()
            this.showLog = true
        },

        closeActivityLog() {
            this.introHint.register(this.hintsQuestionList)
            this.showLog = false
        }
    }
}
</script>

<style lang="scss">
#quiz-creator-modal {
    #quiz-config {
        pointer-events: none;

        &.show {
            pointer-events: unset;
        }
    }

    .modal {
        background-color: rgba(0, 0, 0, 0);
        transition: background-color 0.3s ease-out;

        &.show {
            background-color: rgba(0, 0, 0, 0.5);
            animation: fadeBg 0.3s ease-out;
            pointer-events: unset;

            > .modal-content {
                opacity: 1;
                transform: translateY(0px);
                animation: showModal 0.3s ease-out;
            }
        }

        &.close {
            > .modal-content {
                animation: closeModal 0.3s ease-out;
            }
        }

        > .bg-backdrop {
            top: 0;
            left: 0;
        }

        > .modal-content {
            border-radius: 4px;
            background-color: #fff;
            transform: translateY(-50px);
            opacity: 0;

            @media screen and (min-width: 768px) {
                width: 90%;
                height: 90%;
            }

            .modal-header {
                border-bottom: 1px solid #ccc;
            }

            .modal-body {
                > div {
                    scroll-behavior: smooth;
                    scrollbar-width: none;

                    &::-webkit-scrollbar {
                        width: 0px;
                    }

                    .input,
                    .textarea {
                        padding: 0.375rem 0.75rem;
                        border: 1px solid #ccc;
                        font-size: 1rem;
                        font-weight: 400;
                        color: #000;
                        text-shadow: none;
                    }

                    .input {
                        height: 40px;
                    }
                }
            }

            .modal-footer {
                border-top: 1px solid #ccc;
            }
        }
    }

    #quiz-type-select {
        > .modal-content {
            max-width: 500px;
            height: auto !important;

            .modal-body {
                .type-select-box {
                    column-gap: 1rem;
                }
            }
        }
    }

    #quiz-config {
        align-items: flex-start !important;

        &.show {
            @media screen and (max-width: 767px) {
                background-color: rgba(0, 0, 0, 0) !important;
            }

            > .modal-content {
                @media screen and (max-width: 767px) {
                    transform: translateY(0px);
                    animation: showModal-mobile 0.5s ease;
                }
            }
        }

        &.close {
            > .modal-content {
                @media screen and (max-width: 767px) {
                    animation: closeModal-mobile 0.5s ease;
                }
            }
        }

        > .modal-content {
            max-width: 1700px;
            width: 100% !important;
            height: 100% !important;
            border-radius: 4px;
            background-color: #e6eaf2;

            @media screen and (max-width: 767px) {
                transform: translateY(100vh);
                opacity: 1;
                border-radius: 0;
            }

            > .modal-header {
                top: 0;
                left: 0;
                background-color: #e6eaf2;
            }

            > .modal-body {
                height: calc(100% - 57px);
            }
        }

        .modal-content {
            .modal-header {
                .close-btn-box {
                    width: 1.5rem;
                    height: 1.5rem;
                }
            }
            .modal-body {
                scrollbar-width: none;

                &::-webkit-scrollbar {
                    width: 0px;
                }
            }
        }

        .image-box {
            max-width: 200px;
            width: 50%;
            height: auto;
            aspect-ratio: 1 / 1;
            border: 1px solid #fff;
            border-radius: 0.5rem;
            background-repeat: no-repeat;
            background-size: contain;
        }

        .v-select {
            padding: 0;

            .vs__selected-options {
                align-items: center;

                .vs__selected {
                    margin: 0;
                }
            }
        }

        .share-modal {
            .modal-backdrop {
                scrollbar-width: none;

                &::-webkit-scrollbar {
                    width: 0px;
                }
            }
        }
    }
}

@keyframes showModal-mobile {
    0% {
        transform: translateY(100vh);
    }
    100% {
        transform: translateY(0px);
    }
}

@keyframes closeModal-mobile {
    0% {
        transform: translateY(0px);
    }
    100% {
        transform: translateY(100vh);
    }
}

@keyframes showModal {
    0% {
        transform: translateY(-50px);
        opacity: 0;
    }
    100% {
        transform: translateY(0px);
        opacity: 1;
    }
}

@keyframes closeModal {
    0% {
        transform: translateY(0px);
        opacity: 1;
    }
    100% {
        transform: translateY(-50px);
        opacity: 0;
    }
}

@keyframes fadeBg {
    0% {
        background-color: rgba(0, 0, 0, 0);
    }
    100% {
        background-color: rgba(0, 0, 0, 0.5);
    }
}
</style>
